import { on } from '@ember/modifier';
import { get } from '@ember/object';
import { service } from '@ember/service';
import type StoreService from '@ember-data/store';
import Component from '@glimmer/component';
import type { Task } from 'ember-concurrency';
import pluralize from 'ember-inflector/lib/helpers/pluralize';
import { eq } from 'ember-truth-helpers';
import AgreementPage from 'garaje/models/agreement-page';
import DeviceConfig from 'garaje/models/config';
import Flow from 'garaje/models/flow';
import type GlobalSettingBatch from 'garaje/models/global-setting-batch';
import type { AllPropagableModel } from 'garaje/models/global-setting-batch';
import Location from 'garaje/models/location';
import PhotoPage from 'garaje/models/photo-page';
import type SignInField from 'garaje/models/sign-in-field';
import SummaryPage from 'garaje/models/summary-page';
import type CurrentLocationService from 'garaje/services/current-location';
import type FlashMessagesService from 'garaje/services/flash-messages';
import type GlobalSettingBatchService from 'garaje/services/global-setting-batch';
import { reads } from 'macro-decorators';

interface PropagableSettingsChangedModalChangesConfirmSignature {
  Args: {
    selectedChildren: AllPropagableModel[];
    globalSettingBatch: GlobalSettingBatch;
    forType: string;
    close: () => void;
    backToSearcher: () => void;
    overwriteTask: Task<void, []>;
  };
}

export default class PropagableSettingsChangedModalChangesConfirm extends Component<PropagableSettingsChangedModalChangesConfirmSignature> {
  @service declare currentLocation: CurrentLocationService;
  @service declare flashMessages: FlashMessagesService;
  @service declare store: StoreService;
  @service('global-setting-batch') declare globalSettingBatchService: GlobalSettingBatchService;

  @reads('args.globalSettingBatch.parent.content.name') parentName!: string;
  @reads('args.globalSettingBatch.totalSettingsChanged') totalSettingsChanged!: number;

  get settingsList(): Array<string | undefined> {
    const attributeTuples = [
      ['location', Location],
      ['config', DeviceConfig],
      ['flow', Flow],
      ['flow.agreementPage', AgreementPage],
      ['flow.photoPage', PhotoPage],
      ['flow.summaryPage', SummaryPage],
    ] as const;
    return attributeTuples.reduce<Array<string | undefined>>((list, tuple) => {
      const [attr, model] = tuple;
      const updatedAttributes = (get(this.args.globalSettingBatch, `${attr}.updatedAttributes`) as string[]) ?? [];
      return [...list, ...this._translateAttributes(updatedAttributes, model.OVERWRITABLE_SETTINGS)];
    }, []);
  }

  get signInFields(): SignInField[] {
    const ids = this.args.globalSettingBatch.signInFields ?? [];
    return ids
      .map((id) => this.store.peekRecord('sign-in-field', String(id)))
      .filter((f): f is SignInField => f !== null);
  }

  get atName(): string | undefined {
    // eslint-disable-next-line ember/no-get
    const parent = get(this.args.globalSettingBatch, 'parent.content') as GlobalSettingBatch['parent']['content'];
    const locationName = this.currentLocation.location?.name;
    // eslint-disable-next-line ember/no-get
    if (get(parent, 'constructor.modelName') === 'flow') {
      return `${locationName} / ${(parent as Flow).name}`;
    } else {
      return locationName;
    }
  }

  _translateAttributes(attributes: string[], toTranslate: Record<string, string>): Array<string | undefined> {
    return attributes.map((attribute) => {
      return toTranslate[attribute];
    });
  }

  <template>
    <div data-test-propagable-changes-confirm class="propagable--changes-confirm">
      <header>
        <h2 class="mb-8">
          <span data-test-title>
            Overwrite
            {{pluralize this.totalSettingsChanged "setting"}}
          </span>
          <button
            {{on "click" @close}}
            class="btn btn--small btn--outline btn--cancel float-right box-border
              {{if @overwriteTask.isRunning 'disabled'}}"
          >
            &times;
          </button>
        </h2>
      </header>
      <div data-test-settings-list class="settings-list">
        <p>You've made changes to the following settings at <strong data-test-at-name>{{this.atName}}</strong>.</p>
        <ul>
          {{#each this.settingsList as |setting|}}
            {{#if (eq @forType "flows")}}
              <li>{{this.parentName}} Sign-in flow <i>{{setting}}</i></li>
            {{else}}
              <li>{{setting}}</li>
            {{/if}}
          {{/each}}
          {{#each this.signInFields as |field|}}
            <li><strong>Sign-in field</strong>: {{field.name}}</li>
          {{/each}}
        </ul>
      </div>
      <footer>
        <p data-test-sure-message>
          Are you sure you'd like to update
          {{if (eq this.totalSettingsChanged 1) "this" "these"}}
          {{pluralize this.totalSettingsChanged "setting" without-count=true}}
          at the following
          {{pluralize @selectedChildren.length "location"}}?
        </p>
        <button
          data-test-back-to-searcher
          {{on "click" @backToSearcher}}
          class="btn btn--brand btn--brandOutlineFill {{if @overwriteTask.isRunning 'disabled'}}"
        >
          Back to locations
        </button>
        <button
          data-test-overwrite
          {{on "click" @overwriteTask.perform}}
          class="btn btn--brand float-right box-border {{if @overwriteTask.isRunning 'disabled'}}"
        >

          {{#if @overwriteTask.isRunning}}
            Overwriting...
          {{else}}
            Yes, overwrite
          {{/if}}
        </button>
      </footer>
    </div>
  </template>
}
